import { gpsDriveGet, gpsDrivePut } from "@/lib/api/gpsdrive-client";

// Vínculo usuário↔veículo — Frontend → GPS Drive Backend → Traccar (nunca
// Frontend → Traccar direto para esta operação, ver relatório da etapa).
// O backend resolve inteiramente o isolamento por empresa (o Traccar não
// tem esse conceito) antes de tocar em qualquer permissão real — ver
// src/permissions/permissions.service.ts no gpsdrive-backend.
//
// As duas telas do frontend (Usuários → Veículos e Veículos → Vincular
// usuário) usam exatamente este service — mesma relação Traccar, uma
// única implementação, ver PermissionLinkPicker.
interface TraccarUserRef {
  id: number;
  name: string;
  email: string;
}
export interface TraccarDeviceRef {
  id: number;
  name: string;
  uniqueId?: string;
  attributes?: Record<string, unknown>;
}

// Formato de GET /api/v1/vehicles (gpsdrive-backend, Fase 1 — já em
// produção na branch release/fase1-fase2-grants-sync): frota inteira da
// empresa do chamador (via vehicle_metadata.company_id), restrito a
// Administrator/Manager (403 para Operator/Viewer — a lista fica vazia
// nesse caso, ver useCompanyFleet). Mesmo objeto agregado de
// GET /vehicles/:id/profile; só os campos usados aqui (aba "Veículos" de
// Editar Usuário) são extraídos abaixo.
interface VehicleProfileResponse {
  id: number;
  tracking: {
    name: string;
    plate: string;
    uniqueId: string;
    attributes: Record<string, unknown>;
  };
}

export const vehiclePermissionsService = {
  usersForVehicle: (deviceId: number) =>
    gpsDriveGet<TraccarUserRef[]>(`/api/v1/permissions/vehicles/${deviceId}/users`),

  syncVehicleUsers: (deviceId: number, traccarUserIds: number[]) =>
    gpsDrivePut<TraccarUserRef[]>(`/api/v1/permissions/vehicles/${deviceId}/users`, {
      traccarUserIds,
    }),

  // Veículos JÁ vinculados a um usuário (tc_user_device) — nunca a frota
  // inteira da empresa, ver vehiclesForCompany para isso.
  vehiclesForUser: (traccarUserId: number) =>
    gpsDriveGet<TraccarDeviceRef[]>(`/api/v1/permissions/users/${traccarUserId}/vehicles`),

  syncUserVehicles: (traccarUserId: number, deviceIds: number[]) =>
    gpsDrivePut<TraccarDeviceRef[]>(`/api/v1/permissions/users/${traccarUserId}/vehicles`, {
      deviceIds,
    }),

  // Frota da empresa (Fase 1) — fonte para a lista de checkboxes da aba
  // "Veículos" de Editar Usuário. Nunca useVehicles()/GET /api/devices, que
  // reflete a sessão de quem está logado, não a empresa do usuário sendo
  // editado (ver diagnóstico 2026-08-26).
  //
  // includeUnclaimed=1 (2026-09-30): também traz os veículos sem dono —
  // cadastrados direto no rastreamento e nunca abertos em Editar veículo —
  // que antes não apareciam na busca, mesmo podendo ser vinculados.
  vehiclesForCompany: () =>
    gpsDriveGet<VehicleProfileResponse[]>("/api/v1/vehicles?includeUnclaimed=1").then((rows) =>
      rows.map(
        (r): TraccarDeviceRef => ({
          id: r.id,
          name: r.tracking.name,
          uniqueId: r.tracking.uniqueId,
          attributes: { plate: r.tracking.plate, ...r.tracking.attributes },
        }),
      ),
    ),

  // Posição real de UM veículo da empresa (Âncora, pedido 2026-09-04, aba
  // VEÍCULOS de Editar Usuário) — mesmo endpoint agregado de
  // vehiclesForCompany acima (GET /vehicles/:id/profile), já isolado por
  // empresa no backend (403 se o veículo for de outra empresa). Inclui
  // `status` (2026-09-04, mesmo objeto que routes/index.tsx#VehicleStatus
  // já usa) pra recusar ativar/reancorar quando o veículo está offline —
  // resto do objeto (vehicle/tracker) não é tipado de propósito, essa tela
  // não usa.
  vehiclePosition: (deviceId: number) =>
    gpsDriveGet<{
      tracking: { latitude: number | null; longitude: number | null; status: string };
    }>(`/api/v1/vehicles/${deviceId}/profile`).then((r) => ({
      lat: r.tracking.latitude ?? 0,
      lng: r.tracking.longitude ?? 0,
      offline: r.tracking.status === "offline",
    })),
};
